import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  Mail, 
  Lock, 
  User, 
  Phone, 
  MapPin, 
  FileText, 
  ArrowRight,
  Eye,
  EyeOff,
  CheckCircle,
  Copy,
  AlertCircle,
  Truck
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import CountryCodeSelector from '../ui/CountryCodeSelector';
import authService from '../../services/authService';

const HospitalRegistrationForm = ({ onSuccess, onError, isLoading, setIsLoading }) => {
  const [formData, setFormData] = useState({
    // Hospital Information
    name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    licenseNumber: '',
    operator_id: '',
    
    // Admin Information
    admin_name: '',
    admin_email: '',
    admin_phone: '',
    admin_phone_country_code: '+91', // Default to India
    admin_temporary_password: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [registrationResult, setRegistrationResult] = useState(null);
  const [fleetOperators, setFleetOperators] = useState([]);
  const [loadingOperators, setLoadingOperators] = useState(true);

  // Fetch fleet operators on component mount
  useEffect(() => {
    const fetchFleetOperators = async () => {
      try {
        setLoadingOperators(true);
        // getAllFleetOperators() only returns one page (10 by default) —
        // this walks every page so the dropdown shows every operator.
        const response = await authService.getAllFleetOperatorsUnpaginated();

        if (response.success && response.data) {
          setFleetOperators(response.data);
          // Set first operator as default if available
          if (response.data.length > 0) {
            setFormData(prev => ({
              ...prev,
              operator_id: response.data[0].operator_id
            }));
          }
        }
      } catch (error) {
        // Handle error silently or show user-friendly message
      } finally {
        setLoadingOperators(false);
      }
    };

    fetchFleetOperators();
  }, []); // Removed onError dependency as it's not a primitive

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Generate a random temporary password
  const generateTemporaryPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({
      ...prev,
      admin_temporary_password: password
    }));
  };

  const copyToClipboard = (text) => {
    // This is a safe way to copy in an iFrame
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      // Handle copy error silently
    }
    document.body.removeChild(textarea);
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Hospital name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Hospital name is required';
    } else if (formData.name.trim().length < 2 || formData.name.trim().length > 50) {
      newErrors.name = 'Hospital name must be between 2-50 characters';
    } else if (!/^[a-zA-Z\s\-']+$/.test(formData.name)) {
      newErrors.name = 'Hospital name can only contain letters, spaces, hyphens, and apostrophes';
    }
    
    // Address validation
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    } else if (formData.address.trim().length < 10 || formData.address.trim().length > 200) {
      newErrors.address = 'Address must be between 10-200 characters';
    }
    
    // City validation
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    } else if (formData.city.trim().length < 2 || formData.city.trim().length > 50) {
      newErrors.city = 'City must be between 2-50 characters';
    }
    
    // State validation
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    } else if (formData.state.trim().length < 2 || formData.state.trim().length > 50) {
      newErrors.state = 'State must be between 2-50 characters';
    }
    
    // Pincode validation
    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Pincode must be exactly 6 digits';
    }
    
    // License number validation
    if (!formData.licenseNumber.trim()) {
      newErrors.licenseNumber = 'License number is required';
    } else if (formData.licenseNumber.trim().length < 5 || formData.licenseNumber.trim().length > 20) {
      newErrors.licenseNumber = 'License number must be between 5-20 characters';
    }
    
    // Operator ID validation
    if (!formData.operator_id.trim()) {
      newErrors.operator_id = 'Fleet operator is required';
    }
    
    // Admin name validation
    if (!formData.admin_name.trim()) {
      newErrors.admin_name = 'Admin name is required';
    } else if (formData.admin_name.trim().length < 2 || formData.admin_name.trim().length > 50) {
      newErrors.admin_name = 'Admin name must be between 2-50 characters';
    } else if (!/^[a-zA-Z\s\-']+$/.test(formData.admin_name)) {
      newErrors.admin_name = 'Admin name can only contain letters, spaces, hyphens, and apostrophes';
    }
    
    // Admin email validation
    if (!formData.admin_email.trim()) {
      newErrors.admin_email = 'Admin email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email)) {
      newErrors.admin_email = 'Please enter a valid email address';
    }
    
    // Admin phone validation
    if (!formData.admin_phone.trim()) {
      newErrors.admin_phone = 'Admin phone is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.admin_phone)) {
      newErrors.admin_phone = 'Please enter a valid 10-digit mobile number starting with 6-9';
    }
    
    // Temporary password validation
    if (!formData.admin_temporary_password.trim()) {
      newErrors.admin_temporary_password = 'Temporary password is required';
    } else if (formData.admin_temporary_password.trim().length < 6) {
      newErrors.admin_temporary_password = 'Temporary password must be at least 6 characters';
    }
    
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setErrors({}); // Clear previous errors
    
    try {
      // Prepare data for hospital registration
      const hospitalData = {
        name: formData.name.trim(),
        address: formData.address.trim(), 
        city: formData.city.trim(),
        state: formData.state.trim(),
        pincode: parseInt(formData.pincode.trim(), 10),
        licenseNumber: formData.licenseNumber.trim(),
        operator_id: formData.operator_id.trim(),
        admin_name: formData.admin_name.trim(),
        admin_email: formData.admin_email.trim(),
        admin_phone: formData.admin_phone.trim(), // ✅ Keep as string
        admin_phone_country_code: formData.admin_phone_country_code, // ✅ Add country code
        admin_password: formData.admin_temporary_password
      };
      
      const result = await authService.registerHospital(hospitalData);
      
      if (result.success) {
        // Store the registration result for display
        setRegistrationResult({
          type: 'hospital',
          data: result.data,
          temporaryPassword: formData.admin_temporary_password
        });
        
        // Call onSuccess with the result
        // This is a placeholder for a parent component function
        if (onSuccess) {
           onSuccess({
              ...result,
              type: 'hospital',
              temporaryPassword: formData.admin_temporary_password
           });
        }
       
      }
    } catch (error) {
      // Handle specific error cases
      const errorMessage = error.response?.data?.message || error.message || 'Hospital registration failed. Please try again.';
      // Note: Using a custom message box or toast instead of alert()
      if (onError) {
        onError(errorMessage);
      }
      
    } finally {
      setIsLoading(false);
    }
  };

  // If registration is successful, show success screen
  if (registrationResult) {
    return (
      <Card className="w-full">
        <CardHeader className="text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
            <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-lg sm:text-xl font-bold text-green-900 text-center">Hospital Registered Successfully!</h1>
          </div>
          <CardDescription className="text-xs sm:text-sm text-gray-600 px-2">
            The hospital has been registered and the admin user account has been created.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Hospital Information */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
            <h3 className="font-semibold text-green-900 mb-2 text-sm sm:text-base">Hospital Details</h3>
            <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-green-800">
              <p><strong>Name:</strong> <span className="break-words">{registrationResult.data.hospital?.name}</span></p>
              <p><strong>License:</strong> {formData.licenseNumber}</p>
              <p><strong>Location:</strong> {formData.city}, {formData.state}</p>
            </div>
          </div>

          {/* Admin Login Credentials */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
            <h3 className="font-semibold text-blue-900 mb-2 text-sm sm:text-base">Admin Login Credentials</h3>
            <div className="space-y-2 sm:space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <span className="text-xs sm:text-sm text-blue-800 w-16 flex-shrink-0">Email:</span>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <code className="text-xs sm:text-sm bg-white border px-2 py-1 rounded flex-1 break-all">{formData.admin_email}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(formData.admin_email)}
                    className="h-7 w-7 sm:h-8 sm:w-8 p-0 flex-shrink-0"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <span className="text-xs sm:text-sm text-blue-800 w-16 flex-shrink-0">Password:</span>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <code className="text-xs sm:text-sm bg-white border px-2 py-1 rounded flex-1 font-mono">
                    {showPassword ? registrationResult.temporaryPassword : '••••••••'}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    className="h-7 w-7 sm:h-8 sm:w-8 p-0 flex-shrink-0"
                  >
                    {showPassword ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(registrationResult.temporaryPassword)}
                    className="h-7 w-7 sm:h-8 sm:w-8 p-0 flex-shrink-0"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-amber-900 mb-2 text-sm sm:text-base">Important Instructions</h3>
                <ul className="text-xs sm:text-sm text-amber-800 space-y-1">
                  <li>• The hospital user can now login using the email and temporary password above</li>
                  <li>• On first login, they will be required to reset their password</li>
                  <li>• After password reset, they will need to verify with OTP (bypass code: 123456)</li>
                  <li>• Once verified, they will have full access to the hospital dashboard</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-xs sm:text-sm text-gray-600">
              Redirecting to dashboard in 3 seconds...
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      {/* <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-blue-600" />
          Hospital Registration
        </CardTitle>
        <CardDescription>
          Register a new hospital and create an admin user account
        </CardDescription>
      </CardHeader> */}
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Hospital Information Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b">
              <Building2 className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Hospital Information</h3>
            </div>
            
            {/* Hospital Name and License */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium text-gray-700">
                  Hospital Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Enter hospital name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={errors.name ? 'border-red-300 focus:ring-red-500' : ''}
                  disabled={isLoading}
                />
                {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <label htmlFor="licenseNumber" className="text-sm font-medium text-gray-700">
                  License Number *
                </label>
                <Input
                  id="licenseNumber"
                  name="licenseNumber"
                  type="text"
                  placeholder="Hospital license number"
                  value={formData.licenseNumber}
                  onChange={handleInputChange}
                  className={errors.licenseNumber ? 'border-red-300 focus:ring-red-500' : ''}
                  disabled={isLoading}
                />
                {errors.licenseNumber && <p className="text-sm text-red-600">{errors.licenseNumber}</p>}
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <label htmlFor="address" className="text-sm font-medium text-gray-700">
                Address *
              </label>
              <Textarea
                id="address"
                name="address"
                rows="2"
                placeholder="Enter complete address"
                value={formData.address}
                onChange={handleInputChange}
                className={errors.address ? 'border-red-300 focus:ring-red-500' : ''}
                disabled={isLoading}
              />
              {errors.address && <p className="text-sm text-red-600">{errors.address}</p>}
            </div>

            {/* Location and Fleet Operator */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label htmlFor="city" className="text-sm font-medium text-gray-700">
                  City *
                </label>
                <Input
                  id="city"
                  name="city"
                  type="text"
                  placeholder="City"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={errors.city ? 'border-red-300 focus:ring-red-500' : ''}
                  disabled={isLoading}
                />
                {errors.city && <p className="text-sm text-red-600">{errors.city}</p>}
              </div>

              <div className="space-y-2">
                <label htmlFor="state" className="text-sm font-medium text-gray-700">
                  State *
                </label>
                <Input
                  id="state"
                  name="state"
                  type="text"
                  placeholder="State"
                  value={formData.state}
                  onChange={handleInputChange}
                  className={errors.state ? 'border-red-300 focus:ring-red-500' : ''}
                  disabled={isLoading}
                />
                {errors.state && <p className="text-sm text-red-600">{errors.state}</p>}
              </div>

              <div className="space-y-2">
                <label htmlFor="pincode" className="text-sm font-medium text-gray-700">
                  Pincode *
                </label>
                <Input
                  id="pincode"
                  name="pincode"
                  type="text"
                  placeholder="123456"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  className={errors.pincode ? 'border-red-300 focus:ring-red-500' : ''}
                  disabled={isLoading}
                />
                {errors.pincode && <p className="text-sm text-red-600">{errors.pincode}</p>}
              </div>
            </div>

            {/* Fleet Operator */}
            <div className="space-y-2">
              <label htmlFor="operator_id" className="text-sm font-medium text-gray-700">
                Fleet Operator *
              </label>
              <Select 
                value={formData.operator_id} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, operator_id: value }))}
                disabled={isLoading || loadingOperators}
              >
                <SelectTrigger className={errors.operator_id ? 'border-red-300 focus:ring-red-500' : ''}>
                  <Truck className="w-4 h-4 mr-2" />
                  <SelectValue placeholder={loadingOperators ? 'Loading operators...' : 'Select Fleet Operator'} />
                </SelectTrigger>
                <SelectContent>
                  {fleetOperators.map((operator) => (
                    <SelectItem key={operator.operator_id} value={operator.operator_id}>
                      {operator.name} ({operator.city || 'No City'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.operator_id && <p className="text-sm text-red-600">{errors.operator_id}</p>}
            </div>
          </div>

          {/* Admin User Information Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b">
              <User className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Admin User Information</h3>
            </div>
            
            {/* Admin Details Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2 sm:space-y-3">
                <label htmlFor="admin_name" className="text-xs sm:text-sm font-medium text-gray-700">
                  Admin Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                  <Input
                    id="admin_name"
                    name="admin_name"
                    type="text"
                    placeholder="Enter admin name"
                    value={formData.admin_name}
                    onChange={handleInputChange}
                    className={`pl-10 sm:pl-12 text-xs sm:text-sm ${errors.admin_name ? 'border-red-300 focus:ring-red-500' : ''}`}
                    disabled={isLoading}
                  />
                </div>
                {errors.admin_name && (
                  <p className="text-xs sm:text-sm text-red-600">{errors.admin_name}</p>
                )}
              </div>

              <div className="space-y-2 sm:space-y-3">
                <label htmlFor="admin_email" className="text-xs sm:text-sm font-medium text-gray-700">
                  Admin Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                  <Input
                    id="admin_email"
                    name="admin_email"
                    type="email"
                    placeholder="Enter admin email"
                    value={formData.admin_email}
                    onChange={handleInputChange}
                    className={`pl-10 sm:pl-12 text-xs sm:text-sm ${errors.admin_email ? 'border-red-300 focus:ring-red-500' : ''}`}
                    disabled={isLoading}
                  />
                </div>
                {errors.admin_email && (
                  <p className="text-xs sm:text-sm text-red-600">{errors.admin_email}</p>
                )}
              </div>
            </div>

            {/* Admin Phone Field - Full width outside grid */}
            <div className="space-y-2">
              <label htmlFor="admin_phone" className="text-sm font-medium text-gray-700">
                Admin Phone *
              </label>
              <div className="flex">
                <div className="w-20">
                  <CountryCodeSelector
                    value={formData.admin_phone_country_code}
                    onChange={(e) => handleInputChange({ target: { name: 'admin_phone_country_code', value: e.target.value } })}
                    disabled={isLoading}
                    placeholder="Country"
                  />
                </div>
                <div className="flex-1 relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="admin_phone"
                    name="admin_phone"
                    type="tel"
                    placeholder="Enter admin phone number"
                    value={formData.admin_phone}
                    onChange={handleInputChange}
                    className={`pl-10 ${errors.admin_phone ? 'border-red-300 focus:ring-red-500' : ''}`}
                    disabled={isLoading}
                  />
                </div>
              </div>
              {errors.admin_phone && (
                <p className="text-sm text-red-600">{errors.admin_phone}</p>
              )}
            </div>

            {/* Temporary Password Field - Moved outside the grid */}
            <div className="space-y-2">
              <label htmlFor="admin_temporary_password" className="text-sm font-medium text-gray-700">
                Temporary Password *
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="admin_temporary_password"
                    name="admin_temporary_password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Set temporary password"
                    value={formData.admin_temporary_password}
                    onChange={handleInputChange}
                    className={`pr-10 ${errors.admin_temporary_password ? 'border-red-300 focus:ring-red-500' : ''}`}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  type="button"
                  onClick={generateTemporaryPassword}
                  variant="outline"
                  className="whitespace-nowrap"
                  disabled={isLoading}
                >
                  Generate
                </Button>
              </div>
              {errors.admin_temporary_password && (
                <p className="text-sm text-red-600">{errors.admin_temporary_password}</p>
              )}
              <p className="text-xs text-amber-600">
                ⚠️ This is a temporary password. The user will be required to change it on first login.
              </p>
            </div>
          </div>


          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Registering Hospital...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-2">
                <span>Register Hospital</span>
                <ArrowRight className="h-4 w-4" />
              </div>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default HospitalRegistrationForm;